import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import {
    AllFeedbackDetailsApiInterface,
    SubjectFeedback,
    TermFeedback
} from "@/ts/interfaces/Reporting/SubjectFeedbackInterface";
import { GradeSubjectFeedbackApiInterface } from "@/ts/interfaces/Reporting/api/GradeSubjectFeedbackApiInterface";
import { StudentFeedbackDetailsResponse } from "@/ts/interfaces/Reporting/api/StudentFeedbackApiDetailInterface";
import { SubjectFeedbackForGradeApiInterface } from "@/ts/interfaces/Reporting/api/SubjectFeedbackForGradeApiInterface";
import { mapAllFeedbackDetailsApiData } from "@/utils/reportingDataMapper";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
    getAllFeedbackDetailsThunk,
    getCourseFeedbackWithAnswersThunk,
    getGradeSubjectFeedbackWithAnswersThunk,
    getStudentFeedbackDetailsThunk,
    getSubjectFeedbackForGradeWithAnswersThunk,
    getSubjectFeedbackWithAnswersThunk
} from "../thunks";

/** Reusable request sub-state shape to match the mapping used elsewhere */
type RequestState<T> = {
    data: T;
    status: REQUEST_STATUS;
    error: string | null;
};

const emptyReq = <T>(data: T): RequestState<T> => ({
    data,
    status: REQUEST_STATUS.IDLE,
    error: null
});

interface StudentFeedbackDetailsState {
    /** Mirrors earlier top-level `status` used by selectors (kept for compatibility) */
    status: REQUEST_STATUS;

    /** Unified request states */
    feedbackDetails: RequestState<StudentFeedbackDetailsResponse | null>;
    subjectFeedbackWithAnswers: RequestState<StudentFeedbackDetailsResponse | null>;
    gradeSubjectFeedbackWithAnswers: RequestState<GradeSubjectFeedbackApiInterface | null>;
    subjectFeedbackForGradeWithAnswers: RequestState<SubjectFeedbackForGradeApiInterface | null>;
    allFeedbackDetails: RequestState<AllFeedbackDetailsApiInterface | null>;
    courseFeedbackWithAnswers: RequestState<StudentFeedbackDetailsResponse | null>;

    /** Derived / mapped extras */
    subjectFeedback: SubjectFeedback | null;
    termFeedback: TermFeedback | null;
    isAllFeedbacksCompleted: boolean;
}

const initialState: StudentFeedbackDetailsState = {
    status: REQUEST_STATUS.IDLE,

    feedbackDetails: emptyReq<StudentFeedbackDetailsResponse | null>(null),
    subjectFeedbackWithAnswers: emptyReq<StudentFeedbackDetailsResponse | null>(
        null
    ),
    gradeSubjectFeedbackWithAnswers:
        emptyReq<GradeSubjectFeedbackApiInterface | null>(null),
    subjectFeedbackForGradeWithAnswers:
        emptyReq<SubjectFeedbackForGradeApiInterface | null>(null),
    allFeedbackDetails: emptyReq<AllFeedbackDetailsApiInterface | null>(null),
    courseFeedbackWithAnswers: emptyReq<StudentFeedbackDetailsResponse | null>(
        null
    ),

    subjectFeedback: null,
    termFeedback: null,
    isAllFeedbacksCompleted: false
};

const reportingFeedbackDetailsSlice = createSlice({
    name: "reporting/feedbackDetails",
    initialState,
    reducers: {
        resetStudentFeedbackDetailsState: () => initialState
    },
    extraReducers: builder => {
        // --- Student Feedback Details ---
        builder
            .addCase(getStudentFeedbackDetailsThunk.pending, state => {
                state.feedbackDetails.status = REQUEST_STATUS.PENDING;
                state.feedbackDetails.error = null;
            })
            .addCase(
                getStudentFeedbackDetailsThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<StudentFeedbackDetailsResponse>
                ) => {
                    state.feedbackDetails.status = REQUEST_STATUS.SUCCESS;
                    state.feedbackDetails.data = action.payload;
                }
            )
            .addCase(
                getStudentFeedbackDetailsThunk.rejected,
                (state, { payload }) => {
                    state.feedbackDetails.status = REQUEST_STATUS.ERROR;
                    state.feedbackDetails.error = payload ?? "Unknown error";
                    state.feedbackDetails.data = null;
                }
            );

        // --- Subject Feedback With Answers ---
        builder
            .addCase(getSubjectFeedbackWithAnswersThunk.pending, state => {
                state.subjectFeedbackWithAnswers.status =
                    REQUEST_STATUS.PENDING;
                state.subjectFeedbackWithAnswers.error = null;
                // Keep legacy top-level status in sync (back-compat)
                state.status = REQUEST_STATUS.PENDING;
            })
            .addCase(
                getSubjectFeedbackWithAnswersThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<StudentFeedbackDetailsResponse>
                ) => {
                    state.subjectFeedbackWithAnswers.status =
                        REQUEST_STATUS.SUCCESS;
                    state.subjectFeedbackWithAnswers.data = action.payload;
                    state.subjectFeedbackWithAnswers.error = null;
                    state.status = REQUEST_STATUS.SUCCESS;
                }
            )
            .addCase(
                getSubjectFeedbackWithAnswersThunk.rejected,
                (state, { payload }) => {
                    state.subjectFeedbackWithAnswers.status =
                        REQUEST_STATUS.ERROR;
                    state.subjectFeedbackWithAnswers.error =
                        payload ?? "Unknown error";
                    state.subjectFeedbackWithAnswers.data = null;
                    state.status = REQUEST_STATUS.ERROR;
                }
            );

        // --- Grade Subject Feedback With Answers ---
        builder
            .addCase(getGradeSubjectFeedbackWithAnswersThunk.pending, state => {
                state.gradeSubjectFeedbackWithAnswers.status =
                    REQUEST_STATUS.PENDING;
                state.gradeSubjectFeedbackWithAnswers.error = null;
                // Keep legacy top-level status in sync (back-compat)
                state.status = REQUEST_STATUS.PENDING;
            })
            .addCase(
                getGradeSubjectFeedbackWithAnswersThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<GradeSubjectFeedbackApiInterface>
                ) => {
                    state.gradeSubjectFeedbackWithAnswers.status =
                        REQUEST_STATUS.SUCCESS;
                    state.gradeSubjectFeedbackWithAnswers.data = action.payload;
                    state.gradeSubjectFeedbackWithAnswers.error = null;
                    state.status = REQUEST_STATUS.SUCCESS;
                }
            )
            .addCase(getGradeSubjectFeedbackWithAnswersThunk.rejected, (state, { payload }) => {
                if (!state.gradeSubjectFeedbackWithAnswers) {
                    state.gradeSubjectFeedbackWithAnswers = {
                        data: null,
                        status: REQUEST_STATUS.IDLE,
                        error: null
                    };
                }
                state.gradeSubjectFeedbackWithAnswers.status = REQUEST_STATUS.ERROR;
                state.gradeSubjectFeedbackWithAnswers.error = payload ?? "Unknown error";
                state.gradeSubjectFeedbackWithAnswers.data = null;
                state.status = REQUEST_STATUS.ERROR;
            });

        // --- Subject Feedback for Grade With Answers ---
        builder
            .addCase(
                getSubjectFeedbackForGradeWithAnswersThunk.pending,
                state => {
                    state.subjectFeedbackForGradeWithAnswers.status =
                        REQUEST_STATUS.PENDING;
                    state.subjectFeedbackForGradeWithAnswers.error = null;
                    // Keep legacy top-level status in sync (back-compat)
                    state.status = REQUEST_STATUS.PENDING;
                }
            )
            .addCase(
                getSubjectFeedbackForGradeWithAnswersThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<SubjectFeedbackForGradeApiInterface>
                ) => {
                    state.subjectFeedbackForGradeWithAnswers.status =
                        REQUEST_STATUS.SUCCESS;
                    state.subjectFeedbackForGradeWithAnswers.data = action.payload;
                    state.subjectFeedbackForGradeWithAnswers.error = null;
                    state.status = REQUEST_STATUS.SUCCESS;
                }
            )
            .addCase(
                getSubjectFeedbackForGradeWithAnswersThunk.rejected,
                (state, { payload }) => {
                    console.log("state.subjectFeedbackForGradeWithAnswers ", state.subjectFeedbackForGradeWithAnswers);
                    state.subjectFeedbackForGradeWithAnswers.status =
                        REQUEST_STATUS.ERROR;
                    state.subjectFeedbackForGradeWithAnswers.error =
                        payload ?? "Unknown error";
                    state.subjectFeedbackForGradeWithAnswers.data = null;
                    state.status = REQUEST_STATUS.ERROR;
                }
            );

        // --- All Feedback Details (plus mapped extras) ---
        builder
            .addCase(getAllFeedbackDetailsThunk.pending, state => {
                state.allFeedbackDetails.status = REQUEST_STATUS.PENDING;
                state.allFeedbackDetails.error = null;
            })
            .addCase(
                getAllFeedbackDetailsThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<AllFeedbackDetailsApiInterface>
                ) => {
                    state.allFeedbackDetails.status = REQUEST_STATUS.SUCCESS;
                    state.allFeedbackDetails.data = action.payload;

                    const mapped = mapAllFeedbackDetailsApiData(action.payload);
                    state.subjectFeedback = mapped.subjectFeedback;
                    state.termFeedback = mapped.termFeedback;
                    state.isAllFeedbacksCompleted =
                        mapped.isAllFeedbacksCompleted;
                }
            )
            .addCase(
                getAllFeedbackDetailsThunk.rejected,
                (state, { payload }) => {
                    state.allFeedbackDetails.status = REQUEST_STATUS.ERROR;
                    state.allFeedbackDetails.error = payload ?? "Unknown error";
                    state.allFeedbackDetails.data = null;

                    // Clear derived fields to avoid stale UI
                    state.subjectFeedback = null;
                    state.termFeedback = null;
                    state.isAllFeedbacksCompleted = false;
                }
            );

        // --- Course Feedback With Answers ---
        builder
            .addCase(getCourseFeedbackWithAnswersThunk.pending, state => {
                state.courseFeedbackWithAnswers.status = REQUEST_STATUS.PENDING;
                state.courseFeedbackWithAnswers.error = null;
            })
            .addCase(
                getCourseFeedbackWithAnswersThunk.fulfilled,
                (
                    state,
                    action: PayloadAction<StudentFeedbackDetailsResponse>
                ) => {
                    state.courseFeedbackWithAnswers.status =
                        REQUEST_STATUS.SUCCESS;
                    state.courseFeedbackWithAnswers.data = action.payload;
                }
            )
            .addCase(
                getCourseFeedbackWithAnswersThunk.rejected,
                (state, { payload }) => {
                    state.courseFeedbackWithAnswers.status =
                        REQUEST_STATUS.ERROR;
                    state.courseFeedbackWithAnswers.error =
                        payload ?? "Unknown error";
                    state.courseFeedbackWithAnswers.data = null;
                }
            );
    }
});

export const { resetStudentFeedbackDetailsState } =
    reportingFeedbackDetailsSlice.actions;

export default reportingFeedbackDetailsSlice.reducer;
